import Vue from "vue";
import App from "./App.vue";
import "./registerServiceWorker";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";
import VueApexCharts from "vue-apexcharts";
import {
  auth,
  onAuthStateChanged,
  GoogleAuthProvider,
  firestore,
  doc,
  getDoc,
} from "@/firebase/init";
import InstantSearch from "vue-instantsearch";
import VueConfetti from "vue-confetti";

// Components
import Loading from "@/components/Loading";
Vue.component("Loading", Loading);
import DelayedTooltip from "@/components/misc/ui/DelayedTooltip";
import GroupByHeaderRow from "@/components/misc/ui/GroupByHeaderRow";
import AccountViewerLabel from "@/components/misc/ui/AccountViewerLabel";
import Toast from "@/components/misc/ui/Toast";
import SalesOrderViewerLabel from "@/components/misc/ui/SalesOrderViewerLabel";
import RecurringDatePicker from "@/components/misc/ui/RecurringDatePicker";
import StyledButton from "@/components/misc/ui/buttons/StyledButton.vue";
import AccountCell from "@/components/misc/ui/AccountCell.vue";
import AccountLogoImage from "@/components/misc/ui/AccountLogoImage.vue";

import ThreeColumnLayout from "@/layouts/ThreeColumnLayout.vue";
import TwoColumnLayout from "@/layouts/TwoColumnLayout.vue";
import MapView from "@/components/api/google-maps/map/MapView.vue";
import RemindersList from "@/components/misc/ui/RemindersList.vue";
import FilesList from "@/components/misc/ui/FilesList.vue";
import ContactsList from "@/components/misc/ui/ContactsList.vue";
import SegmentedPickerTabs from "@/components/misc/ui/tabs/SegmentedPickerTabs.vue";
import SalesOrderTable from "@/components/misc/ui/tables/SalesOrderTable.vue";
import DefaultTable from "@/components/misc/ui/tables/DefaultTable.vue";
import StatusChip from "@/components/misc/ui/chips/StatusChip.vue";
import BorderedAvatar from "@/components/misc/ui/chips/BorderedAvatar.vue";
import AvatarListCell from "@/components/misc/ui/lists/AvatarListCell.vue";

Vue.component("DelayedTooltip", DelayedTooltip);
Vue.component("GroupByHeaderRow", GroupByHeaderRow);
Vue.component("AccountViewerLabel", AccountViewerLabel);
Vue.component("AccountLogoImage", AccountLogoImage);
Vue.component("RecurringDatePicker", RecurringDatePicker);
Vue.component("Toast", Toast);
Vue.component("SalesOrderViewerLabel", SalesOrderViewerLabel);
Vue.component("ApexChart", VueApexCharts);
Vue.component("StyledButton", StyledButton);
Vue.component("AccountCell", AccountCell);
Vue.component("ThreeColumnLayout", ThreeColumnLayout);
Vue.component("TwoColumnLayout", TwoColumnLayout);
Vue.component("MapView", MapView);
Vue.component("RemindersList", RemindersList);
Vue.component("FilesList", FilesList);
Vue.component("ContactsList", ContactsList);
Vue.component("SegmentedPickerTabs", SegmentedPickerTabs);
Vue.component("SalesOrderTable", SalesOrderTable);
Vue.component("DefaultTable", DefaultTable);
Vue.component("StatusChip", StatusChip);
Vue.component("BorderedAvatar", BorderedAvatar);
Vue.component("AvatarListCell", AvatarListCell);
// Libraries
Vue.use(require("vue-moment"));
Vue.use(VueApexCharts);
Vue.use(InstantSearch);
Vue.use(VueConfetti);

// Utils
import CacheManager from "@/utils/CacheManager";
const cacheManager = new CacheManager("logoCache", 1800000); // 30-minute TTL
Vue.prototype.$cacheManager = cacheManager;

import ClipboardManager from "@/utils/ClipboardManager";
const clipboardManager = new ClipboardManager();
Vue.prototype.$clipboardManager = clipboardManager;

import FirebaseService from "@/utils/firebaseService.js";
const firebaseServiceUtil = new FirebaseService();
Vue.prototype.$firebaseService = firebaseServiceUtil;

// Filters
import "@/utils/filters";

// Validation Mixin
Vue.mixin({
  methods: {
    cleanedPhoneNumber(value) {
      return String(value).replace(/[^0-9]/g, "");
    },
    isValidEmail(value) {
      const pattern =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return pattern.test(value) ? true : false;
    },
    isValidPhoneNumber(value) {
      const pattern = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
      return pattern.test(value) ? true : false;
    },
    //  Dynamic text color based on background color (https://wunnle.com/dynamic-text-color-based-on-background)
    //  - Allows us to increase legibility and accessibility when using dark background colors in tables, cards, etc.
    getRGB(color) {
      return parseInt(color, 16) || color;
    },
    getsRGB(color) {
      return this.getRGB(color) / 255 <= 0.03928
        ? this.getRGB(color) / 255 / 12.92
        : Math.pow((this.getRGB(color) / 255 + 0.055) / 1.055, 2.4);
    },
    getLuminance(hexColor) {
      return (
        0.2126 * this.getsRGB(hexColor.substr(1, 2)) +
        0.7152 * this.getsRGB(hexColor.substr(3, 2)) +
        0.0722 * this.getsRGB(hexColor.substr(-2))
      );
    },
    getContrast(foreground, background) {
      const luminance1 = this.getLuminance(foreground);
      const luminance2 = this.getLuminance(background);
      return (
        (Math.max(luminance1, luminance2) + 0.05) /
        (Math.min(luminance1, luminance2) + 0.05)
      );
    },
    getTextColor(bgColor) {
      const whiteContrast = this.getContrast(bgColor, "#ffffff");
      const blackContrast = this.getContrast(bgColor, "#000000");

      return whiteContrast > blackContrast ? "#ffffff" : "#000000";
    },
    async delay(ms) {
      return new Promise((res) => setTimeout(res, ms));
    },
  },
  computed: {
    deviceSize() {
      return {
        mobile: this.$vuetify.breakpoint.xsOnly,
        tablet: this.$vuetify.breakpoint.smAndDown,
        laptop: this.$vuetify.breakpoint.mdAndDown,
        desktop: this.$vuetify.breakpoint.lgAndDown,
        xLarge: this.$vuetify.breakpoint.xlOnly,
      };
    },
    defaultBorderColor() {
      return "var(--v-border-base)";
    },
    isMac() {
      if (navigator?.userAgentData?.platform?.length) {
        // Use userAgentData if available
        const platform = navigator.userAgentData.platform;
        return platform.toLowerCase().includes("mac");
      } else {
        // Fallback to userAgent if userAgentData is not supported
        const userAgent = navigator.userAgent.toLowerCase();
        return userAgent.includes("macintosh");
      }
    },
    metaKey() {
      let cmd = {
        name: "Command",
        abbreviation: "Cmd",
        icon: "mdi-apple-keyboard-command",
        text: "⌘",
        key: "meta",
      };
      let ctrl = {
        name: "Control",
        abbreviation: "Ctrl",
        icon: "mdi-apple-keyboard-control",
        text: "^",
        key: "control",
      };
      return this.isMac ? cmd : ctrl;
    },
    keyboardShortcuts() {
      let navigationSearch = {
        text: "Open/Close Navigation",
        keys: [this.metaKey, "/"],
      };
      let accountSearch = {
        text: "Search Accounts",
        keys: [this.metaKey, "K"],
      };
      let themeToggle = {
        text: "Toggle Light/Dark Mode",
        keys: [this.metaKey, "D"],
      };
      return { navigationSearch, accountSearch, themeToggle };
    },
  },
});

Vue.config.productionTip = false;

let app = null;
onAuthStateChanged(auth, async (user) => {
  if (user) {
    const { claims } = await auth.currentUser.getIdTokenResult();
    const userDoc = doc(firestore, "users", user.uid);
    const userResponse = await getDoc(userDoc);

    store.commit("setUser", {
      uid: user.uid,
      accessToken: user.accessToken,
      providers: user.providerData,
      name: {
        first: user.displayName.split(" ")[0].toLowerCase(),
        last: user.displayName.split(" ")[1].toLowerCase(),
      },
      initials: user.displayName
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .toLowerCase(),
      phoneNumber: user.phoneNumber || "",
      email: user.email,
      brands: claims.brands,
      accountType: claims.accountType,
      country: claims.country,
      state: claims.state,
      role: claims.role,
      // flags,
      dashboards: claims.dashboards ? claims.dashboards : [],
      views: claims.views ? claims.views : [],
      ...userResponse.data(),
    });
  }

  if (!app) {
    app = new Vue({
      router,
      store,
      vuetify,
      render: (h) => h(App),
    });
  }
  app.$mount("#app");
});
